import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus } from 'types';
import { Checkbox } from 'antd';
import { useArchivedMultiSelect } from 'hooks/useArchivedMultiSelect';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';
import FormSelect from 'components/Select/FormSelect';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	setIDsOfRestaurantsWhereToHide: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	IDsOfRestaurantsWhereToHide: string[];
	IDsSelectedRestaurant: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	setIDsOfRestaurantsWhereToHide,
	IDSelectedCustomer,
	IDsOfRestaurantsWhereToHide,
	IDsSelectedRestaurant,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const restToHide = useMemo(
		() => data.filter((el) => IDsSelectedRestaurant.includes(el.id)),
		[data, IDsSelectedRestaurant],
	);

	const { showArchived, toggleShowArchived, visibleItems, options } = useArchivedMultiSelect({
		items: restToHide,
		entityType: 'restaurants',
		selectedIds: IDsOfRestaurantsWhereToHide,
	});

	const filteredData = useMemo(() => {
		return searchString
			? options.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	}, [searchString, options]);

	const handleSelectAllData = useCallback(() => {
		if (visibleItems.length && IDsOfRestaurantsWhereToHide.length === visibleItems.length) {
			setIDsOfRestaurantsWhereToHide([]);
		} else {
			setIDsOfRestaurantsWhereToHide(visibleItems.map((el) => el.id));
		}
	}, [visibleItems, IDsOfRestaurantsWhereToHide, setIDsOfRestaurantsWhereToHide]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={
								!!(visibleItems.length && IDsOfRestaurantsWhereToHide.length === visibleItems.length)
							}
							disabled={!visibleItems.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleItems,
			IDsOfRestaurantsWhereToHide,
			handleSelectAllData,
			searchString,
			setSearchString,
			showArchived,
			toggleShowArchived,
		],
	);

	useEffect(() => {
		setSearchString('');
	}, [IDSelectedCustomer, IDsSelectedRestaurant]);

	return (
		<FormSelect
			testId="MultipleSelect"
			name="IDsOfRestaurantsWhereToHide"
			label="restaurantsLabel"
			options={filteredData}
			disabled={!IDsSelectedRestaurant.length}
			onChange={setIDsOfRestaurantsWhereToHide}
			mode="multiple"
			counterValue={IDsOfRestaurantsWhereToHide.length}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(MultipleSelect);
